import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { useFilterEvents } from '@/lib/api/hooks/event';
import { Loader } from '@/components/ui/Loader';
import DashboardCard from '@/pages/dashboard/DashboardCard';

interface RecentEventItem {
	id: string;
	title: string;
	image: string;
	date: string;
	author: string;
}

const RecentEvents = () => {
	const navigate = useNavigate();
	const { data: eventsData, isLoading, error } = useFilterEvents(
		{ page: 1, size: 3 },
		{ status: 'PUBLISH' }
	);

	if (error) {
		console.error('Error fetching events:', error);
	}

	const recentEvents = useMemo<RecentEventItem[]>(() => {
		if (error || !eventsData?.data?.data) return [];
		const eventList = Array.isArray(eventsData.data.data) ? eventsData.data.data : [];

		return eventList.map((event: { eventId: string; eventTitle: string; imageUrl?: string; datePublished?: string; createdAt: string; username?: string }) => ({
			id: event.eventId,
			title: event.eventTitle,
			image: event.imageUrl || 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image',
			date: new Date(event.datePublished || event.createdAt).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
			author: event.username || 'Admin',
		}));
	}, [eventsData, error]);

	return (
		<DashboardCard
			title="Recent Events"
			titleIcon={<CalendarDays className="h-4 w-4 text-gray-500" />}
			headerAction={
				<Link to="/admin/events" className="text-sm font-semibold text-[#681628] hover:underline">
					View all
				</Link>
			}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader size="md" variant="wine" />
				</div>
			) : recentEvents.length > 0 ? (
				<div className="space-y-3">
					{recentEvents.map((event: RecentEventItem, index: number) => (
						<div
							key={event.id}
							className={`flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors ${index < recentEvents.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}
							onClick={() => navigate(`/admin/events/${event.id}`)}
						>
							<img
								src={event.image}
								alt={event.title}
								className="w-14 h-10 object-cover rounded-md flex-shrink-0"
							/>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-gray-800 text-xs line-clamp-2">{event.title}</h3>
								<p className="text-xs text-gray-500 mt-0.5">{event.date} | {event.author}</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="text-center py-8">
					<p className="text-sm text-gray-500">No recent events</p>
				</div>
			)}
		</DashboardCard>
	);
};

export default RecentEvents;